import { Process } from '@/modules/Processes/domain';
import {
  automatedColumnConfig,
  businessAreaNameColumnConfig,
  codeColumnConfig,
  deleteRowColumnConfig,
  emptyColumnConfig,
  nameColumnConfig,
  periodColumnConfig,
  processCompanyNameColumnConfig,
  runTimeColumnConfig,
  statusColumnConfig,
} from '@/utils';
import { dividerColumnConfig } from '@/utils/tableColumns/divider-column-config';
import { totalEstimatedCostColumnConfig } from '@/utils/tableColumns/total-estimated-cost-column-config';
import { totalUsersCountColumnConfig } from '@/utils/tableColumns/total-users-count-column-config';
import { totalWorkloadColumnConfig } from '@/utils/tableColumns/total-workload-column-config';
import { twMerge } from 'tailwind-merge';
import { OverflowTooltip } from '..';
import { TableColumn, TableRow } from '../Table/types';
import { ProcessWithWorkflows } from './types';

type BaseColumnsConfig = {
  onClick?: (row?: TableRow) => void;
  onDeleteClick?: (row?: TableRow) => void;
  deleteDisableMessage?: string;
  context?: 'organization' | 'company' | 'businessArea';
};

type ProcessColumnsConfig = BaseColumnsConfig & {
  context?: 'organization' | 'company' | 'businessArea';
};

type WorkflowColumnsConfig = BaseColumnsConfig;

type TaskColumnsConfig = BaseColumnsConfig;

const commomColumnConfig = {
  sortable: false,
  className: 'max-w-[9.09%] min-w-[9.09%]',
};

const commomTasksColumnConfig = {
  sortable: false,
  className: 'max-w-[4vw] min-w-[4vw]',
};

export const getProcessesColumns = ({
  onClick,
  onDeleteClick,
  deleteDisableMessage,
  context = 'organization',
}: ProcessColumnsConfig = {}): Array<TableColumn<TableRow>> => {
  return [
    nameColumnConfig(
      {
        ...commomColumnConfig,
        render: (value, row) => (
          <div
            className="flex items-center gap-2.5 overflow-hidden"
            onClick={() => onClick?.(row)}
          >
            <OverflowTooltip text={value} textClassName="font-black" />
          </div>
        ),
      },
      'process',
    ),
    context === 'organization'
      ? processCompanyNameColumnConfig(commomColumnConfig)
      : emptyColumnConfig('1', commomColumnConfig),
    ['organization', 'company'].includes(context)
      ? businessAreaNameColumnConfig(commomColumnConfig)
      : emptyColumnConfig('2', commomColumnConfig),
    codeColumnConfig(commomColumnConfig),
    statusColumnConfig(commomColumnConfig),
    runTimeColumnConfig(commomColumnConfig),
    dividerColumnConfig(),
    totalUsersCountColumnConfig(commomColumnConfig),
    totalWorkloadColumnConfig(commomColumnConfig),
    totalEstimatedCostColumnConfig(commomColumnConfig),
    onDeleteClick
      ? deleteRowColumnConfig({
          onDeleteClick: onDeleteClick,
          disabledMessage: deleteDisableMessage,
        })
      : emptyColumnConfig('3', commomColumnConfig),
  ];
};

export const getWorkflowsColumns = ({
  onClick,
}: WorkflowColumnsConfig = {}): Array<TableColumn<TableRow>> => {
  return [
    nameColumnConfig(
      {
        ...commomColumnConfig,
        colSpan: 3,
        className: twMerge(commomColumnConfig.className, 'pl-11'),
        render: (value, row) => (
          <div
            className="flex items-center gap-2.5 overflow-hidden"
            onClick={() => onClick?.(row)}
          >
            <OverflowTooltip text={value} textClassName="font-black" />
          </div>
        ),
      },
      'workflow',
    ),
    codeColumnConfig(commomColumnConfig),
    statusColumnConfig(commomColumnConfig),
    runTimeColumnConfig(commomColumnConfig),
    dividerColumnConfig(),
    totalUsersCountColumnConfig(commomColumnConfig),
    totalWorkloadColumnConfig(commomColumnConfig),
    totalEstimatedCostColumnConfig(commomColumnConfig),
    emptyColumnConfig('3', commomColumnConfig),
  ];
};

export const getTasksColumns = ({ onClick }: TaskColumnsConfig = {}): Array<
  TableColumn<TableRow>
> => {
  return [
    nameColumnConfig(
      {
        ...commomTasksColumnConfig,
        className: twMerge(
          commomTasksColumnConfig.className,
          'max-w-[6.2vw] min-w-[6.2vw]',
          'ultrawide:max-w-[7.3vw] ultrawide:min-w-[7.3vw]',
        ),
        colSpan: 2,
        render: (value, row) => (
          <div
            className="flex items-center gap-2.5 overflow-hidden"
            onClick={() => onClick?.(row)}
          >
            <OverflowTooltip text={value} textClassName="font-black" />
          </div>
        ),
      },
      'task',
    ),
    periodColumnConfig(commomTasksColumnConfig),
    codeColumnConfig({
      ...commomTasksColumnConfig,
      className: twMerge(
        commomTasksColumnConfig.className,
        'max-w-[4.6vw] min-w-[4.6vw]',
        'ultrawide:max-w-[4.4vw] ultrawide:min-w-[4.4vw]',
      ),
    }),
    statusColumnConfig({
      ...commomTasksColumnConfig,
      className: twMerge(
        commomTasksColumnConfig.className,
        'max-w-[3.6vw] min-w-[3.6vw]',
      ),
    }),
    automatedColumnConfig({
      ...commomTasksColumnConfig,
      className: twMerge(
        commomTasksColumnConfig.className,
        'max-w-[4.4vw] min-w-[4.4vw]',
      ),
    }),
    dividerColumnConfig({
      key: 'divider-1',
      options: { className: twMerge(commomTasksColumnConfig.className, 'p-0') },
    }),
    totalUsersCountColumnConfig({
      ...commomTasksColumnConfig,
      acessorKey: 'usersCount',
    }),
    totalWorkloadColumnConfig({
      ...commomTasksColumnConfig,
      acessorKey: 'workload',
    }),
    totalEstimatedCostColumnConfig({
      ...commomTasksColumnConfig,
      acessorKey: 'estimatedCost',
    }),
    emptyColumnConfig('1', commomTasksColumnConfig),
  ];
};

const mapProcessModelIntoProcessWithWorkflows = (
  process: Process,
): ProcessWithWorkflows => ({
  id: process.id,
  name: process.name,
  code: process.code,
  status: process.status,
  runTime: process.runTime,
  organization: process.organization,
  company: process.company,
  businessAreaName: process.businessAreaName,
  canDelete: process.canDelete,
  totalWorkload: process.totalWorkload,
  totalEstimatedCost: process.totalEstimatedCost,
  totalUsers: process.totalUsers,
  rows:
    process.workflows?.map((workflow) => ({
      id: workflow.id,
      name: workflow.name,
      code: workflow.code,
      order: workflow.order,
      status: workflow.status,
      runTime: workflow.runTime,
      totalUsers: workflow.totalUsers,
      totalWorkload: workflow.totalWorkload,
      totalEstimatedCost: workflow.totalEstimatedCost,
      rows:
        workflow.tasks?.map((task) => ({
          id: task.id,
          name: task.name,
          code: task.code,
          status: task.status,
          usersCount: task.usersCount,
          periodicityId: task.periodicityId,
          workload: task.workload,
          estimatedCost: task.estimatedCost,
          automated: task.automated,
        })) ?? [],
    })) ?? [],
});

export const mapProcessesModelIntoProcessWithWorkflows = (
  processes: Process[],
) => processes?.map(mapProcessModelIntoProcessWithWorkflows) ?? [];
